const LAB_DISPLAY_TIME_ZONE = "Asia/Tokyo";

/* 2000年より前と1万年以降は記事の日付としてありえない。秒で来た値は1970年になるのでここで落ちる */
const LAB_DATE_MIN_MS = Date.UTC(2000, 0, 1);
const LAB_DATE_MAX_MS = Date.UTC(10000, 0, 1);

const ISO_DATE =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?(Z|[+-]\d{2}:?\d{2})?$/;

/*
  記事の日時を UTC のミリ秒で返す。読めない値は undefined。

  本番の api-go は publishedAt と updatedAt を Go の time.RFC3339 で返す
  (graph/mappers.go の timePtrToRFC3339Ptr。2026-09-23 に /lab と記事ページの
  loader データでも "2026-09-23T02:05:21Z" の形を確かめた)。Hono の頃の経路は
  ミリ秒の数字で返していたので、数字はミリ秒として読む。

  - オフセットの無い ISO は UTC として読む。Date に任せると SSR が動く
    環境の時間帯で値が変わる
  - 暦に無い日付 (2026-02-31 など) は翌月へ繰り越さず捨てる
  - 範囲外は捨てる。new Date(1e20).toISOString() は投げて meta ごと落ちる
*/
function parseLabDate(value: string | null | undefined): number | undefined {
  if (!value) return undefined;
  let ms: number;
  if (/^\d+$/.test(value)) {
    ms = Number(value);
  } else {
    const m = ISO_DATE.exec(value);
    if (!m) return undefined;
    const [, y, mo, d, h = "00", mi = "00", s = "00", offset] = m;
    const day = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
    if (
      day.getUTCFullYear() !== Number(y) ||
      day.getUTCMonth() !== Number(mo) - 1 ||
      day.getUTCDate() !== Number(d) ||
      Number(h) > 23 ||
      Number(mi) > 59 ||
      Number(s) > 59
    ) {
      return undefined;
    }
    const normalized = `${y}-${mo}-${d}T${h}:${mi}:${s}${value.match(/\.\d+/)?.[0] ?? ""}${offset ?? "Z"}`;
    ms = Date.parse(normalized);
  }
  if (!Number.isFinite(ms) || ms < LAB_DATE_MIN_MS || ms >= LAB_DATE_MAX_MS) {
    return undefined;
  }
  return ms;
}

export function formatLabArticleDate(value: string | null) {
  const ms = parseLabDate(value);
  if (ms === undefined) return "YUMESAKU LAB";
  const date = new Date(ms);
  return new Intl.DateTimeFormat("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: LAB_DISPLAY_TIME_ZONE,
  })
    .format(date)
    .replaceAll("/", ".");
}

/* 並べ替え用。読めない値は 0 にして末尾へ送る */
export function labDateValue(value: string | null) {
  return parseLabDate(value) ?? 0;
}

/* meta と JSON-LD に書く日時。読めないときは undefined を返し、呼ぶ側は欄ごと出さない */
export function labDateISO(value: string | null | undefined) {
  const ms = parseLabDate(value);
  return ms === undefined ? undefined : new Date(ms).toISOString();
}

const MONTHS = "JanFebMarAprMayJunJulAugSepOctNovDec";
const X_CREATED_AT =
  /^[A-Z][a-z]{2} ([A-Z][a-z]{2}) (\d{2}) (\d{2}:\d{2}:\d{2}) ([+-]\d{2})(\d{2}) (\d{4})$/;

/*
  投稿の日時を <time dateTime> に書く ISO にする。読めない値は undefined。
  X の created_at は "Wed Aug 26 02:37:50 +0000 2026" の形で、ISO ではない。
  Date に任せると読めるかどうかが実行環境しだいなので、ISO に組み直してから
  記事と同じ parseLabDate を通す。オフセットの無い値を UTC として読むのも、
  暦に無い日付や時刻を捨てるのもそちらに任せる
*/
export function labPostDateISO(value: string): string | undefined {
  const m = X_CREATED_AT.exec(value);
  if (!m) return labDateISO(value);
  const [, month, day, time, offsetHour, offsetMinute, year] = m;
  const monthIndex = MONTHS.indexOf(month);
  if (monthIndex < 0 || monthIndex % 3 !== 0) return undefined;
  const mm = String(monthIndex / 3 + 1).padStart(2, "0");
  return labDateISO(`${year}-${mm}-${day}T${time}${offsetHour}:${offsetMinute}`);
}

export function formatLabPostDate(value: string) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("ja-JP", {
    month: "short",
    day: "numeric",
    timeZone: LAB_DISPLAY_TIME_ZONE,
  }).format(date);
}
